import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFloppyDisk, FaSpinner, FaCheck } from 'react-icons/fa6';
import { TimeInformationDatabaseData } from '../../../../types/common';
import { useTimeInformation, useTimeInfoValidation } from './hooks';
import { TimeFieldRow } from './TimeFieldRow';
import { AdditionalFieldsPanel } from './AdditionalFieldsPanel';
import { CheckboxFieldConfig } from './types';
import { TIMEZONE_OPTIONS } from './constants';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

// Maps time field IDs to their violin plot component names
const TIME_COMPONENT_MAP: Record<string, string[]> = {
  jahr: ['Y_sin', 'Y_cos'],
  monat: ['M_sin', 'M_cos'],
  woche: ['W_sin', 'W_cos'],
  tag: ['D_sin', 'D_cos'],
  feiertag: ['H'],
};

interface TimeInformationInputProps {
  onChange?: (data: TimeInformationDatabaseData) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  sessionId?: string;
  inputFileCount?: number;
}

/**
 * Time Information Input component
 * Refactored from monolithic ~627 line component into modular composition
 *
 * Structure:
 * - useTimeInformation hook: state management + fetch + handlers
 * - useTimeInfoAutoSave hook: auto-save with 500ms debounce
 * - TimeFieldRow: table row with checkbox
 * - AdditionalFieldsPanel: expanded panel with detailed fields
 */
export const TimeInformationInput: React.FC<TimeInformationInputProps> = ({ onChange, onDirtyChange, sessionId, inputFileCount = 0 }) => {
  const { t } = useTranslation('training');

  // Main state and handlers from custom hook
  const timeInfo = useTimeInformation(sessionId, onChange, onDirtyChange);

  // Validation hook
  const validation = useTimeInfoValidation(timeInfo.state);

  // Checkbox field configuration
  const checkboxFields: CheckboxFieldConfig[] = useMemo(() => [
    { id: 'jahr', label: t('timeInformation.fields.jahr'), tooltip: t('tooltips.zeitinformationen.jahr') },
    { id: 'monat', label: t('timeInformation.fields.monat'), tooltip: t('tooltips.zeitinformationen.monat') },
    { id: 'woche', label: t('timeInformation.fields.woche'), tooltip: t('tooltips.zeitinformationen.woche') },
    { id: 'tag', label: t('timeInformation.fields.tag'), tooltip: t('tooltips.zeitinformationen.tag') },
    { id: 'feiertag', label: t('timeInformation.fields.feiertag'), tooltip: t('tooltips.zeitinformationen.feiertag') }
  ], [t]);

  // Calculate IN labels matching violin plot order (alphabetically sorted components)
  const timeFieldLabels = useMemo(() => {
    const fieldOrder = ['jahr', 'monat', 'woche', 'tag', 'feiertag'] as const;

    // Collect all active components with their field ID
    const allComponents: { fieldId: string; comp: string }[] = [];
    for (const fieldId of fieldOrder) {
      if (timeInfo.state[fieldId].checked) {
        for (const comp of TIME_COMPONENT_MAP[fieldId]) {
          allComponents.push({ fieldId, comp });
        }
      }
    }

    // Sort alphabetically (matches violin plot display order)
    allComponents.sort((a, b) => a.comp.localeCompare(b.comp));

    // Assign IN numbers and group by field
    const labels: Record<string, string[]> = {};
    allComponents.forEach(({ fieldId, comp }, i) => {
      if (!labels[fieldId]) labels[fieldId] = [];
      labels[fieldId].push(`IN${inputFileCount + i + 1} (${comp})`);
    });

    // Join each field's labels
    const result: Record<string, string> = {};
    for (const [fieldId, parts] of Object.entries(labels)) {
      result[fieldId] = parts.join('  ');
    }
    return result;
  }, [inputFileCount, timeInfo.state]);

  const hasAnyZeitumstellung = Object.values(timeInfo.state).some(
    (field) => typeof field === 'object' && 'checked' in field && field.checked && 'data' in field && field.data.detaillierteBerechnung
  );

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible mb-4 sm:mb-6">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <div className="flex items-center justify-center gap-1.5">
          <h2 className="text-base font-semibold text-foreground tracking-tight">
            {t('timeInformation.title')}
          </h2>
          <InfoTooltip text={t('tooltips.zeitinformationen.section')} width="w-96" />
          {sessionId && (
            <button
              type="button"
              onClick={timeInfo.saveData}
              disabled={timeInfo.isSaving || timeInfo.isLoading || !timeInfo.isDirty || !validation.isValid}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-150 ${
                timeInfo.isSaving
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : !validation.isValid
                    ? 'bg-red-50 text-red-400 cursor-not-allowed'
                    : timeInfo.isDirty
                      ? 'bg-amber-50 text-amber-600 hover:bg-amber-100 active:scale-[0.95]'
                      : 'bg-emerald-50 text-emerald-600'
              }`}
              title={
                !validation.isValid
                  ? t('timeInformation.validation.fixErrors')
                  : timeInfo.isDirty
                    ? t('csvFileUploader.table.saveChanges')
                    : t('csvFileUploader.messages.fileUploaded')
              }
            >
              {timeInfo.isSaving ? (
                <FaSpinner className="w-3.5 h-3.5 animate-spin" />
              ) : timeInfo.isDirty ? (
                <FaFloppyDisk className="w-3.5 h-3.5" />
              ) : (
                <FaCheck className="w-3.5 h-3.5" />
              )}
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Loading state */}
        {timeInfo.isLoading && (
          <div className="flex items-center justify-center gap-2 p-3 bg-accent/60 border border-primary/20 rounded-lg mb-4">
            <FaSpinner className="animate-spin text-primary text-xs" />
            <span className="text-xs text-primary">{t('timeInformation.loading')}</span>
          </div>
        )}

        {/* Error message */}
        {timeInfo.errorMessage && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200/80 rounded-lg mb-4">
            <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-red-500 text-[10px] font-bold">!</span>
            </div>
            <p className="text-sm text-red-600 leading-relaxed flex-1">
              {t('timeInformation.error', { message: timeInfo.errorMessage })}
            </p>
          </div>
        )}

        {/* Time field rows */}
        <div className="space-y-2 max-w-2xl mx-auto">
          {checkboxFields.map(({ id, label, tooltip }) => (
            <TimeFieldRow
              key={id}
              id={id}
              label={label}
              checked={timeInfo.state[id].checked}
              onToggle={() => timeInfo.handleCheckboxChange(id)}
              isLoading={timeInfo.isLoading}
              hasErrors={!validation.fieldValidations[id].isValid}
              indexLabel={timeFieldLabels[id]}
              tooltip={tooltip}
            >
              <AdditionalFieldsPanel
                fieldId={id}
                fieldData={timeInfo.state[id].data}
                isLoading={timeInfo.isLoading}
                onDetailedCalcChange={() => timeInfo.handleDetailedCalcChange(id)}
                onDatenformChange={(e) => timeInfo.handleDatenformChange(id, e)}
                onZeithorizontChange={(fieldId, value) => timeInfo.handleZeithorizontChange(id, fieldId, value)}
                onSkalierungChange={() => timeInfo.handleSkalierungChange(id)}
                onSkalierungValueChange={(fieldId, value) => timeInfo.handleSkalierungValueChange(id, fieldId, value)}
                onLandChange={id === 'feiertag' ? timeInfo.handleLandChange : undefined}
                validationErrors={validation.fieldValidations[id].errors}
              />
            </TimeFieldRow>
          ))}

          {/* Timezone row */}
          {hasAnyZeitumstellung && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 flex-shrink-0">
                  <label
                    htmlFor="time-info-zeitzone"
                    className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                  >
                    {t('timeInformation.fields.zeitzone')}
                  </label>
                  <InfoTooltip text={t('tooltips.zeitinformationen.zeitzone')} width="w-80" />
                </div>
                <select
                  id="time-info-zeitzone"
                  value={timeInfo.state.zeitzone}
                  onChange={(e) => timeInfo.handleTimezoneChange(e.target.value)}
                  className="flex-1 bg-card border border-input rounded-lg px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-shadow duration-150"
                  disabled={timeInfo.isLoading}
                >
                  {TIMEZONE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeInformationInput;
